// _forms/_admin/AdminFormulario.jsx
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import useFormulario from '../_shared/hooks/useFormulario';
import ConditionalModal from '../_shared/components/ConditionalModal';
import { PaymentModal } from '../../../payment';
import { formatters } from '../_shared/utils/formatters';
import { useNotification } from '../../../errnotification/context/NotificationContext';
import DateSelectors from '../_shared/components/DateSelectors';
import BannerRenderer from '../_shared/components/BannerRenderer';
import './AdminFormulario.css';

const AdminFormulario = ({
  isEditable = true,
  selectedPackage = null,
  isFillingForm = false,
  onTabChange,
}) => {
  const { slug } = useParams();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const { showNotification } = useNotification();
  
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Estado para el modal de condiciones de regla de categoría
  const [categoryRuleConditionModal, setCategoryRuleConditionModal] = useState(null);

  const {
    formConfig,
    setFormConfig,
    paquetes,
    selectedPaquete,
    selectedAddons,
    deletedFields,
    setDeletedFields,
    error,
    isLoading,
    resumenModal,
    modalOpen,
    activeModal,
    selectedField,
    existingConditions,
    conditions,
    optionConditionModal,
    setModalOpen,
    setActiveModal,
    setOptionConditionModal,
    setConditions,
    shouldShowOption,
    validateForm,
    handlePaqueteChange,
    handleCloseModal,
    handleButtonClick,
    handleSaveConditions,
    handleSave,
    totalAmount,
    handleTypeChangeWrapper,
    handleFieldChangeWrapper,
    handleLabelChangeWrapper,
    handleAddFieldWrapper,
    handleDeleteFieldWrapper,
    handleAddOptionWrapper,
    handleDeleteOptionWrapper,
    handleOptionChangeWrapper,
    handleOptionConditionsWrapper,
    handleAddCategoryRuleWrapper,
    handleUpdateCategoryRuleWrapper,
    handleDeleteCategoryRuleWrapper,
    handleToggleCategoryRulePackageWrapper,
    handleUpdateCategoryRuleConditionsWrapper,
    handleRequiredToggleWrapper,
  } = useFormulario({
    slug,
    baseUrl,
    selectedPackage,
    isEditable,
    isFillingForm,
    onTabChange,
  });

  // ==========================================
  // FUNCIONES DE DRAG & DROP
  // ==========================================

  const handleReorderFields = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    
    setFormConfig((prevConfig) => {
      const fields = [...prevConfig.fields.fields];
      const [movedField] = fields.splice(fromIndex, 1);
      fields.splice(toIndex, 0, movedField);
      
      return {
        ...prevConfig,
        fields: {
          ...prevConfig.fields,
          fields: fields,
        },
      };
    });
    
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (dragIndex !== null && dragIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (dragIndex !== null && dragIndex !== index) {
      handleReorderFields(dragIndex, index);
    }
    
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // ==========================================
  // 1. SECCIÓN DE MONTO
  // ==========================================
  const renderAmountSection = () => {
    if (!selectedPaquete) return null;
    // 🟢 FIX: los paquetes se identifican por "id_package", no por "id"
    // (packagesService nunca devuelve un campo "id"). Comparar por "id"
    // hacía que .find() nunca encontrara el paquete correcto, o encontrara
    // uno equivocado por coincidencia de "undefined" === "undefined".
    const paquete = paquetes.find((p) => String(p.id_package) === String(selectedPaquete));
    if (!paquete) return null;

    const amtPerPerson = parseFloat(paquete.amount) || 0;
    const numCompetitors = parseInt(paquete.number_of_competitors) || 1;
    // 🟢 FIX: el Total no multiplicaba por el número de competidores,
    // mostraba el mismo valor que "Por persona".
    const totalGeneral = amtPerPerson * numCompetitors;

    return (
      <div className="admin-form-field">
        <label className="admin-amount-label">Información del Paquete</label>
        <div className="admin-package-details">
          <div className="admin-package-detail">
            <span className="admin-detail-label">Por persona:</span>
            <span className="admin-detail-value">
              {formatters.formatCurrency(amtPerPerson, paquete.currency)}
            </span>
          </div>
          <div className="admin-package-detail">
            <span className="admin-detail-label">Competidores:</span>
            <span className="admin-detail-value">{numCompetitors}</span>
          </div>
          <div className="admin-package-detail admin-total-amount">
            <span className="admin-detail-label">Total:</span>
            <span className="admin-detail-value admin-total-value">
              {formatters.formatCurrency(totalGeneral, paquete.currency)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // ==========================================
  // 2. RENDERIZADO DE CAMPOS DEL FORMULARIO
  // ==========================================
  const renderFormFields = () => {
    if (!formConfig?.fields?.fields || !Array.isArray(formConfig.fields.fields)) {
      return null;
    }
    
    return formConfig.fields.fields
      .filter((field) => !field.isPackageInclude && !deletedFields.includes(field.id))
      .map((field, index) => {
        const fieldValue =
          field.value !== undefined && field.value !== null ? field.value : '';
        const isInvalid = field.invalid ? 'admin-invalid-field' : '';
        const errorMessage = field.errorMessage || 'Campo con formato inválido';
        
        const isDragging = dragIndex === index;
        const isDragOver = dragOverIndex === index;
        const dragClasses = [
          'admin-form-field',
          isInvalid,
          isDragging ? 'dragging' : '',
          isDragOver ? 'drag-over' : ''
        ].filter(Boolean).join(' ');

        return (
          <div 
            key={field.id || index} 
            className={dragClasses}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            style={{
              opacity: isDragging ? 0.5 : 1,
              transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
              transition: 'all 0.2s ease',
              cursor: 'move'
            }}
          >
            {/* INDICADOR DE ARRASTRE */}
            <div className="admin-drag-handle" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              marginBottom: '8px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px dashed #dee2e6',
              cursor: 'grab',
              userSelect: 'none'
            }}>
              <span style={{ fontSize: '18px', color: '#6c757d' }}>⠿</span>
              <span style={{ fontSize: '12px', color: '#6c757d', fontWeight: '500' }}>
                Arrastra para reordenar
              </span>
              {isDragOver && (
                <span style={{ 
                  fontSize: '11px', 
                  color: '#9370DB', 
                  marginLeft: 'auto',
                  fontWeight: 'bold' 
                }}>
                  Suelta aquí
                </span>
              )}
            </div>

            {/* HEADER: Label, Selector de Migración y Obligatorio */}
            <div className="admin-field-header">
              <input
                type="text"
                value={field.label}
                onChange={(e) => handleLabelChangeWrapper(e, field.id, setFormConfig)}
                className="admin-field-label-input"
                placeholder="Nombre del campo"
              />
              <select
                className="admin-type-selector"
                value={`${field.type}_${field.dataType || 'text'}`}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'date_date')
                    handleTypeChangeWrapper(field.id, { type: 'date', dataType: 'date' }, setFormConfig);
                  else if (val === 'category_text')
                    handleTypeChangeWrapper(field.id, { type: 'category', dataType: 'text' }, setFormConfig);
                  else if (val === 'text_integer')
                    handleTypeChangeWrapper(field.id, { type: 'text', dataType: 'integer' }, setFormConfig);
                  else if (val === 'text_email')
                    handleTypeChangeWrapper(field.id, { type: 'text', dataType: 'email' }, setFormConfig);
                  else if (val === 'text_text')
                    handleTypeChangeWrapper(field.id, { type: 'text', dataType: 'text' }, setFormConfig);
                  else if (val === 'select_null')
                    handleTypeChangeWrapper(field.id, { type: 'select', dataType: null }, setFormConfig);
                  else if (val === 'banner_text')
                    handleTypeChangeWrapper(field.id, { type: 'banner', dataType: 'text' }, setFormConfig);
                }}
              >
                <option value="text_text">📝 Texto Libre</option>
                <option value="text_integer">🔢 Número</option>
                <option value="text_email">📧 Correo</option>
                <option value="date_date">📅 Fecha (Calendario)</option>
                <option value="category_text">🏷️ Categoría (Edad/Élite)</option>
                <option value="select_null">🔽 Lista / Select</option>
                <option value="banner_text">📢 Banner / Texto Informativo</option>
              </select>
              <label className="admin-required-toggle">
                <input
                  type="checkbox"
                  checked={field.required || false}
                  onChange={() => handleRequiredToggleWrapper(field.id, setFormConfig)}
                />
                <span className="admin-required-text">Obligatorio</span>
              </label>
            </div>

            {/* VISTA PREVIA DEL INPUT */}
            {field.type === 'date' && (
              <div className="admin-field-wrapper">
                <DateSelectors
                  value={field.value || ''}
                  onChange={(dateStr) => {
                    const event = {
                      target: {
                        value: dateStr,
                        name: field.label,
                      },
                    };
                    handleFieldChangeWrapper(event, field.id, setFormConfig, isFillingForm);
                  }}
                  required={field.required}
                  disabled={!isEditable && !isFillingForm}
                  className={field.invalid ? 'error' : ''}
                />
                {field.invalid && (
                  <p className="admin-error-message">{field.errorMessage}</p>
                )}
              </div>
            )}

            {field.type === 'category' && (
              <input
                type="text"
                value={
                  isEditable
                    ? 'Auto-calculado según Fecha de Nacimiento y Paquete'
                    : (field.value || 'Sin Categoría')
                }
                disabled
                className="admin-field-input"
              />
            )}

            {field.type === 'text' && field.dataType === 'integer' && (
              <input
                type="number"
                name={field.label}
                value={field.value || ''}
                onChange={(e) => handleFieldChangeWrapper(e, field.id, setFormConfig, isFillingForm)}
                className="admin-field-input"
                placeholder="Solo números"
              />
            )}

            {field.type === 'text' && field.dataType === 'email' && (
              <input
                type="email"
                name={field.label}
                value={field.value || ''}
                onChange={(e) => handleFieldChangeWrapper(e, field.id, setFormConfig, isFillingForm)}
                className="admin-field-input"
                placeholder="ejemplo@correo.com"
              />
            )}

            {field.type === 'text' && !['integer', 'email'].includes(field.dataType) && (
              <input
                type="text"
                name={field.label}
                value={field.value || ''}
                onChange={(e) => handleFieldChangeWrapper(e, field.id, setFormConfig, isFillingForm)}
                className="admin-field-input"
                placeholder={`Ingresa ${field.label.toLowerCase()}`}
              />
            )}

            {field.type === 'select' && (
              <select
                name={field.label}
                value={fieldValue}
                onChange={(e) => handleFieldChangeWrapper(e, field.id, setFormConfig, isFillingForm)}
                className={isInvalid ? 'admin-invalid-select' : 'admin-field-input'}
              >
                <option value="">Seleccione una opción (Vista Previa)</option>
                {field.options
                  .filter((opt) => isEditable || shouldShowOption(opt))
                  .map((opt, optionIndex) => {
                    const label = typeof opt === 'string' ? opt : opt.label;
                    const hasConditions = typeof opt !== 'string' && opt.conditions?.length > 0;
                    return (
                      <option key={optionIndex} value={label}>
                        {hasConditions ? `⚙️ ${label}` : label}
                      </option>
                    );
                  })}
              </select>
            )}

            {field.type === 'banner' && (
              <>
                {isEditable ? (
                  // Modo edición: textarea para que el admin pueda editar
                  <textarea
                    name={field.label}
                    value={field.value || ''}
                    onChange={(e) => handleFieldChangeWrapper(e, field.id, setFormConfig, isFillingForm)}
                    className="admin-field-input admin-banner-textarea"
                    rows={4}
                    placeholder="Contenido o aviso explicativo. Las URLs serán convertidas a enlaces automáticamente."
                  />
                ) : (
                  // Modo vista: renderizado con detección de URLs
                  <BannerRenderer 
                    content={field.value || ''} 
                    className="admin-banner-preview"
                  />
                )}
              </>
            )}

            {/* MENSAJE DE ERROR */}
            {field.invalid && (
              <p className="admin-error-message">⚠️ {errorMessage}</p>
            )}

            {/* ==========================================
                EDITOR DE REGLAS DE CATEGORÍA (ACTUALIZADO)
                ========================================== */}
            {/* 🟢 FIX: este editor es solo para el administrador. Antes se
                renderizaba también cuando isEditable=false (llenado de
                formulario / vista previa), exponiendo controles de admin
                (checkboxes de paquete, Élite/Libre, eliminar regla) a quien
                solo debería ver el campo de categoría auto-calculado. */}
            {field.type === 'category' && isEditable && (
              <div className="admin-category-rules-editor">
                <h5>Rango de Edades y Categorías por Paquete:</h5>
                {(field.categoryRules || []).map((rule, ruleIdx) => {
                  // Obtener los packageIds actuales (convertir a array si es necesario)
                  const rulePackageIds = rule.packageIds && rule.packageIds.length > 0
                    ? rule.packageIds.map(String)
                    : rule.packageId
                    ? [String(rule.packageId)]
                    : [];
                  
                  const hasExtraConditions = rule.conditions && rule.conditions.length > 0;

                  return (
                    <div key={ruleIdx} className="admin-category-rule-row" style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '12px',
                      backgroundColor: '#fafafa'
                    }}>
                      {/* CHECKBOXES PARA SELECCIONAR MÚLTIPLES PAQUETES */}
                      <div className="admin-category-package-checkboxes" style={{
                        marginBottom: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px'
                      }}>
                        <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Paquetes:</span>
                        {paquetes.map((pkg) => {
                          // 🔥 FIX CRÍTICO: Usar id_package o id. 
                          // Si usamos solo id_package y es undefined, String(undefined) se 
                          // convierte en "undefined", haciendo que TODOS los checkboxes 
                          // parezcan seleccionados y se guarden incorrectamente.
                          const pkgId = pkg.id_package || pkg.id;
                          const isChecked = rulePackageIds.includes(String(pkgId));
                          
                          return (
                            <label key={pkgId} style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              cursor: 'pointer',
                              padding: '2px 8px',
                              backgroundColor: isChecked ? '#e3f2fd' : 'transparent',
                              borderRadius: '4px',
                              border: isChecked ? '1px solid #1976d2' : '1px solid #ccc'
                            }}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  handleToggleCategoryRulePackageWrapper(
                                    field.id,
                                    ruleIdx,
                                    pkgId, // 🔥 Pasar el ID válido, no pkg.id_package
                                    setFormConfig
                                  );
                                }}
                              />
                              <span style={{ fontSize: '13px' }}>{pkg.title || pkg.name}</span>
                            </label>
                          );
                        })}
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="text"
                          placeholder="Nombre (ej: Élite / Juvenil)"
                          value={rule.label || ''}
                          onChange={(e) =>
                            handleUpdateCategoryRuleWrapper(field.id, ruleIdx, 'label', e.target.value, setFormConfig)
                          }
                          style={{
                            padding: '6px 10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            flex: '1 1 150px'
                          }}
                        />
                        <input
                          type="number"
                          placeholder="Mín"
                          value={rule.minAge || ''}
                          onChange={(e) =>
                            handleUpdateCategoryRuleWrapper(field.id, ruleIdx, 'minAge', e.target.value, setFormConfig)
                          }
                          style={{
                            padding: '6px 10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            width: '70px'
                          }}
                        />
                        <span>a</span>
                        <input
                          type="number"
                          placeholder="Máx"
                          value={rule.maxAge || ''}
                          onChange={(e) =>
                            handleUpdateCategoryRuleWrapper(field.id, ruleIdx, 'maxAge', e.target.value, setFormConfig)
                          }
                          style={{
                            padding: '6px 10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            width: '70px'
                          }}
                        />
                        <label className="admin-special-checkbox" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <input
                            type="checkbox"
                            checked={rule.isSpecial || false}
                            onChange={(e) =>
                              handleUpdateCategoryRuleWrapper(field.id, ruleIdx, 'isSpecial', e.target.checked, setFormConfig)
                            }
                          />
                          Élite/Libre
                        </label>
                      </div>

                      {/* BOTÓN Y VISUALIZACIÓN DE CONDICIONES EXTRA */}
                      <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => {
                            // Abrir modal de condiciones para esta regla
                            setCategoryRuleConditionModal({
                              fieldId: field.id,
                              ruleIndex: ruleIdx,
                              conditions: rule.conditions || [],
                              operator: rule.conditionOperator || 'OR'
                            });
                          }}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '4px',
                            border: hasExtraConditions ? '1px solid #4caf50' : '1px solid #ccc',
                            backgroundColor: hasExtraConditions ? '#e8f5e9' : 'transparent',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: hasExtraConditions ? '#2e7d32' : '#333'
                          }}
                        >
                          {hasExtraConditions ? '✅ Condiciones extra' : '➕ Agregar condición extra'}
                        </button>
                        {hasExtraConditions && (
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            (Operador: {rule.conditionOperator || 'OR'})
                          </span>
                        )}
                        {hasExtraConditions && rule.conditions && (
                          <span style={{ fontSize: '11px', color: '#888', marginLeft: '4px' }}>
                            {rule.conditions.map((c, idx) => {
                              // Mostrar resumen de la condición
                              let label = '';
                              if (c.fieldId === 'packages' || c.type === 'package') {
                                label = `Paquete = ${c.value || '...'}`;
                              } else if (typeof c.fieldId === 'string' && c.fieldId.startsWith('addon_')) {
                                label = `Addon seleccionado`;
                              } else {
                                const targetField = formConfig?.fields?.fields?.find(f => f.id === parseInt(c.fieldId));
                                label = `${targetField?.label || 'Campo'} = ${c.options?.join(', ') || '...'}`;
                              }
                              return <span key={idx} style={{ marginRight: '4px' }}>📌 {label}</span>;
                            })}
                          </span>
                        )}
                      </div>

                      {/* BOTÓN ELIMINAR REGLA */}
                      <button
                        type="button"
                        onClick={() => handleDeleteCategoryRuleWrapper(field.id, ruleIdx, setFormConfig)}
                        style={{
                          marginTop: '8px',
                          padding: '4px 12px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: '#ffebee',
                          color: '#c62828',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        ✕ Eliminar regla
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={() => handleAddCategoryRuleWrapper(field.id, setFormConfig)}
                  className="admin-add-rule-btn"
                  style={{
                    padding: '8px 16px',
                    border: '2px dashed #9370DB',
                    borderRadius: '6px',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    color: '#9370DB',
                    fontWeight: 'bold',
                    width: '100%'
                  }}
                >
                  + Agregar Categoría
                </button>
              </div>
            )}

            {/* BOTONES DE ACCIÓN DEL CAMPO */}
            <div className="admin-field-actions">
              <button
                type="button"
                className="admin-remove-field-btn"
                onClick={() => handleDeleteFieldWrapper(field.id, setFormConfig, setDeletedFields)}
              >
                🗑️ Eliminar Campo
              </button>
              <button
                className="admin-add-condition-btn"
                onClick={() => handleButtonClick(field)}
              >
                ⚙️ Agregar Condición
              </button>
              {field.fieldConditions && field.fieldConditions.length > 0 && (
                <span className="admin-condition-indicator">
                  ✅ Condiciones aplicadas
                </span>
              )}
            </div>
          </div>
        );
      });
  };

  // ==========================================
  // 3. RENDERIZADO DE OPCIONES DE SELECTS
  // ==========================================
  const renderSelectOptions = () => {
    const fields = formConfig?.fields?.fields || [];
    return fields.map((field) => (
      <div key={field.id} className="admin-field-container">
        {field.type === 'select' && isEditable && (
          <div className="admin-select-options-editor">
            <h4>Opciones del Select "{field.label}":</h4>
            {field.options.map((option, index) => {
              const label = typeof option === 'string' ? option : option.label;
              const hasConditions = typeof option !== 'string' && option.conditions?.length > 0;
              return (
                <div key={index} className="admin-option-row">
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => handleOptionChangeWrapper(field.id, index, e.target.value, setFormConfig)}
                    className="admin-option-input"
                  />
                  <button
                    type="button"
                    className={`admin-option-condition-btn ${hasConditions ? 'admin-has-conditions' : ''}`}
                    title={hasConditions ? 'Editar condiciones' : 'Agregar condición'}
                    onClick={() =>
                      setOptionConditionModal({
                        fieldId: field.id,
                        optionIndex: index,
                        option:
                          typeof option === 'string'
                            ? { label: option, conditions: [], conditionOperator: 'OR' }
                            : option,
                      })
                    }
                  >
                    {hasConditions ? '⚙️ Condición ✓' : '⚙️ Condición'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteOptionWrapper(field.id, index, setFormConfig)}
                    className="admin-delete-option-btn"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => handleAddOptionWrapper(field.id, setFormConfig)}
              className="admin-add-option-btn"
            >
              + Añadir Opción
            </button>
          </div>
        )}
      </div>
    ));
  };

  // ==========================================
  // 4. BOTONES PARA AGREGAR CAMPOS
  // ==========================================
  const renderAddFieldButtons = () => (
    <div className="admin-add-field-buttons">
      <button type="button" className="admin-add-field-btn" onClick={() => handleAddFieldWrapper('text', 'text', setFormConfig)}>
        ➕ Texto
      </button>
      <button type="button" className="admin-add-field-btn" onClick={() => handleAddFieldWrapper('text', 'integer', setFormConfig)}>
        ➕ Número
      </button>
      <button type="button" className="admin-add-field-btn" onClick={() => handleAddFieldWrapper('text', 'email', setFormConfig)}>
        ➕ Correo
      </button>
      <button type="button" className="admin-add-field-btn" onClick={() => handleAddFieldWrapper('date', 'date', setFormConfig)}>
        📅 Fecha
      </button>
      <button type="button" className="admin-add-field-btn" onClick={() => handleAddFieldWrapper('category', 'category', setFormConfig)}>
        🏷️ Categoría
      </button>
      <button type="button" className="admin-add-field-btn" onClick={() => handleAddFieldWrapper('select', null, setFormConfig)}>
        ➕ Select
      </button>
      <button type="button" className="admin-add-field-btn" onClick={() => handleAddFieldWrapper('banner', 'text', setFormConfig)}>
        📢 Banner / Aviso
      </button>
    </div>
  );

  // ==========================================
  // 5. RENDER PRINCIPAL
  // ==========================================
  if (isLoading) return <div className="admin-loading">Cargando...</div>;
  if (error) return <div className="admin-error">{`Error: ${error}`}</div>;

  return (
    <div className="admin-formulario-container" id="admin-formulario">
      <style>{dragDropStyles}</style>
      <h1 className="admin-formulario-titulo">⚙️ Administrar Formulario</h1>

      <div className="admin-paquete-container">
        <label htmlFor="admin-paquete" className="admin-paquete-label">
          Seleccionar Paquete
        </label>
        <select
          id="admin-paquete"
          className="admin-paquete-select"
          value={selectedPaquete}
          onChange={handlePaqueteChange}
        >
          <option value="">Seleccione un paquete</option>
          {paquetes.map((paquete) => (
            // 🟢 FIX: los paquetes se identifican por "id_package", no "id".
            // Con paquete.id (undefined) el <option> quedaba sin "value" real
            // y el navegador usaba el texto visible como value, rompiendo
            // toda comparación posterior (precio, guardado, condiciones).
            <option key={paquete.id_package} value={paquete.id_package}>
              {paquete.title || paquete.name}
            </option>
          ))}
        </select>
      </div>

      {renderAmountSection()}
      
      <div className="admin-age-calculation-config">
        <label className="admin-config-label">
          📅 Método de cálculo de edad:
        </label>
        <select
          className="admin-age-method-select"
          value={formConfig?.ageCalculationMethod || 'current'}
          onChange={(e) => {
            setFormConfig((prev) => ({
              ...prev,
              ageCalculationMethod: e.target.value,
            }));
          }}
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            cursor: 'pointer',
            marginLeft: '10px',
          }}
        >
          <option value="current">📆 Edad actual (hasta hoy)</option>
          <option value="endOfYear">🎄 Edad hasta fin de año</option>
        </select>
        <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '0.8rem' }}>
          💡 "Edad actual": calcula la edad del usuario al día de hoy.<br/>
          💡 "Edad hasta fin de año": calcula la edad que tendrá al 31 de diciembre 
          (usado en eventos deportivos para categorías anuales).
        </small>
      </div>

      <div className="admin-form-fields">{renderFormFields()}</div>

      {renderSelectOptions()}
      {renderAddFieldButtons()}

      <div className="admin-form-actions">
        <button
          type="button"
          className="admin-save-form-btn"
          onClick={async () => {
            if (!validateForm()) {
              showNotification('❌ Por favor corrige los errores en el formulario', 'error', 4000);
              return;
            }
            try {
              const saved = await handleSave();
              if (saved) {
                showNotification('✅ Formulario guardado con éxito', 'success', 3000);
              }
            } catch (err) {
              console.error('Error al guardar:', err);
              showNotification(`❌ Error al guardar: ${err.message}`, 'error', 5000);
            }
          }}
        >
          💾 Guardar Formulario
        </button>
      </div>

      {/* ==========================================
          MODALES
          ========================================== */}

      {/* Modal para condiciones de opciones de select */}
      {optionConditionModal && (
        <ConditionalModal
          isOpen={true}
          onClose={() => setOptionConditionModal(null)}
          fieldId={optionConditionModal.fieldId}
          fields={formConfig.fields.fields}
          paquetes={paquetes || []}
          existingConditions={optionConditionModal.option.conditions || []}
          onSave={(payload) => {
            const newConditions = Array.isArray(payload) ? payload : payload.conditions || [];
            const operator = Array.isArray(payload) ? 'OR' : payload.operator || 'OR';
            handleOptionConditionsWrapper(
              optionConditionModal.fieldId,
              optionConditionModal.optionIndex,
              newConditions,
              operator,
              setFormConfig
            );
            setOptionConditionModal(null);
          }}
        />
      )}

      {/* Modal para condiciones de regla de categoría - CORREGIDO */}
      {categoryRuleConditionModal && (
        <ConditionalModal
          isOpen={true}
          onClose={() => setCategoryRuleConditionModal(null)}
          fieldId={categoryRuleConditionModal.fieldId}
          fields={formConfig.fields.fields}
          paquetes={paquetes || []}
          existingConditions={categoryRuleConditionModal.conditions || []}
          onSave={(payload) => {
            // Extraer condiciones y operador del payload
            let newConditions;
            let operator;
            
            if (Array.isArray(payload)) {
              newConditions = payload;
              operator = 'OR';
            } else {
              newConditions = payload.conditions || [];
              operator = payload.operator || 'OR';
            }
            
            console.log('Guardando condiciones para regla de categoría:', {
              fieldId: categoryRuleConditionModal.fieldId,
              ruleIndex: categoryRuleConditionModal.ruleIndex,
              conditions: newConditions,
              operator: operator
            });
            
            // Llamar al handler del hook
            handleUpdateCategoryRuleConditionsWrapper(
              categoryRuleConditionModal.fieldId,
              categoryRuleConditionModal.ruleIndex,
              newConditions,
              operator,
              setFormConfig
            );
            
            // Cerrar el modal
            setCategoryRuleConditionModal(null);
          }}
        />
      )}

      {/* Modal para condiciones de campo */}
      {activeModal === 'condicional' && (
        <ConditionalModal
          isOpen={true}
          onClose={handleCloseModal}
          fieldId={selectedField}
          fields={formConfig.fields.fields}
          conditions={conditions}
          setConditions={setConditions}
          paquetes={paquetes || []}
          onSave={(payload) => {
            // Guardar condiciones del campo
            const newConditions = Array.isArray(payload) ? payload : payload.conditions || [];
            const operator = Array.isArray(payload) ? 'OR' : payload.operator || 'OR';
            
            setFormConfig((prev) => ({
              ...prev,
              fields: {
                ...prev.fields,
                fields: prev.fields.fields.map((f) =>
                  f.id === selectedField
                    ? {
                        ...f,
                        fieldConditions: newConditions,
                        conditionOperator: operator,
                      }
                    : f
                ),
              },
            }));
            setConditions(newConditions);
            setActiveModal(null);
          }}
          existingConditions={existingConditions}
        />
      )}

      {/* Modal de pago */}
      {modalOpen && resumenModal && (
        <PaymentModal
          resumen={resumenModal}
          onClose={handleCloseModal}
          onSubmit={() => {
            handleCloseModal();
          }}
          onTabChange={onTabChange}
          slug={slug}
        />
      )}
    </div>
  );
};

// Estilos para drag & drop
const dragDropStyles = `
.admin-form-field.dragging {
  opacity: 0.5;
  border: 2px dashed #9370DB;
  background-color: #f8f5ff;
}

.admin-form-field.drag-over {
  border: 2px solid #9370DB;
  background-color: #f0ebff;
  box-shadow: 0 0 0 3px rgba(147, 112, 219, 0.2);
}

.admin-drag-handle:active {
  cursor: grabbing;
}

.admin-form-field {
  transition: all 0.2s ease;
}
`;

export default AdminFormulario;
